import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Search as SearchIcon, Pencil, Trash2, Link2, X } from "lucide-react";

const money = (v) =>
    `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Never render a raw object as a React child — some fields carry denormalized snapshots.
const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

// Only Confirmed counts toward any total, so the badge has to make that obvious.
const statusCls = (st) => {
    switch (String(st || "").toLowerCase()) {
        case "confirmed": return "text-emerald-600 bg-emerald-500/10";
        case "cancelled": return "text-rose-600 bg-rose-500/10";
        default: return "text-amber-600 bg-amber-500/10";
    }
};

const emptyForm = {
    customer_name: "",
    contact_person: "",
    contact_no: "",
    email: "",
    amount: "",
    sale_date: "",
    store: "",
    mode_of_payment: "",
    quotation_no: "",
    po_number: "",
    status: "Draft",
    notes: "",
    lead_id: "",
    lead_record_no: "",
    deal_id: "",
    deal_record_no: "",
};

const PAGE_SIZE = 100;
const today = () => new Date().toISOString().slice(0, 10);

export default function ManualSales() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "owner";

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totals, setTotals] = useState({});
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    const [status, setStatus] = useState("");
    const [store, setStore] = useState("");
    const [agent, setAgent] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sort, setSort] = useState("newest");
    const [q, setQ] = useState("");

    const [facets, setFacets] = useState({ stores: [], agents: [], statuses: [], payment_modes: [], all_stores: [] });

    const [editing, setEditing] = useState(null);   // null | {} (new) | sale (edit)
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async (nextOffset = offset) => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/sales", {
                params: {
                    status: status || undefined,
                    store: store || undefined,
                    agent: agent || undefined,
                    dateFrom: dateFrom || undefined,
                    dateTo: dateTo || undefined,
                    sort,
                    q: q || undefined,
                    limit: PAGE_SIZE,
                    offset: nextOffset,
                },
            });
            setRows(Array.isArray(data?.items) ? data.items : []);
            setTotal(typeof data?.total === "number" ? data.total : 0);
            setTotals(data?.totals || {});
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/crm/sales/facets");
                setFacets({
                    stores: data?.stores || [],
                    agents: data?.agents || [],
                    statuses: data?.statuses || [],
                    payment_modes: data?.payment_modes || [],
                    all_stores: data?.all_stores || [],
                });
            } catch { /* filters just stay empty */ }
        })();
    }, []);

    useEffect(() => {
        setOffset(0);
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, store, agent, dateFrom, dateTo, sort]);

    const search = (e) => {
        e.preventDefault();
        setOffset(0);
        load(0);
    };

    const goPage = (newOffset) => {
        setOffset(newOffset);
        load(newOffset);
    };

    const clearFilters = () => {
        setStatus(""); setStore(""); setAgent(""); setDateFrom(""); setDateTo(""); setQ(""); setSort("newest");
    };

    const hasFilters = status || store || agent || dateFrom || dateTo || q;

    const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const openNew = () => {
        setForm({ ...emptyForm, sale_date: today() });
        setEditing({});
    };

    const openEdit = (sale) => {
        setForm({
            customer_name: s(sale.customer_name, ""),
            contact_person: s(sale.contact_person, ""),
            contact_no: s(sale.contact_no, ""),
            email: s(sale.email, ""),
            amount: sale.amount ?? "",
            sale_date: s(sale.sale_date, ""),
            store: s(sale.store, ""),
            mode_of_payment: s(sale.mode_of_payment, ""),
            quotation_no: s(sale.quotation_no, ""),
            po_number: s(sale.po_number, ""),
            status: s(sale.status, "Draft"),
            notes: s(sale.notes, ""),
            lead_id: s(sale.lead_id, ""),
            lead_record_no: s(sale.lead_record_no, ""),
            deal_id: s(sale.deal_id, ""),
            deal_record_no: s(sale.deal_record_no, ""),
        });
        setEditing(sale);
    };

    const closeModal = () => {
        setEditing(null);
        setForm(emptyForm);
    };

    const save = async () => {
        if (!form.customer_name.trim()) { toast.error("Customer name is required."); return; }
        if (!form.store) { toast.error("Pick a store — it is how the sale is matched to SAP."); return; }
        if (!form.sale_date) { toast.error("Sale date is required."); return; }
        if (!(Number(form.amount) > 0)) { toast.error("Amount must be greater than zero."); return; }

        setSaving(true);
        try {
            const payload = { ...form, amount: Number(form.amount), lead_id: form.lead_id || null, deal_id: form.deal_id || null };
            delete payload.lead_record_no;
            delete payload.deal_record_no;
            if (editing?.id) {
                await api.put(`/crm/sales/${editing.id}`, payload);
                toast.success("Sale updated");
            } else {
                await api.post("/crm/sales", payload);
                toast.success("Sale recorded");
            }
            closeModal();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/crm/sales/${confirmDelete.id}`);
            toast.success("Sale deleted");
            setConfirmDelete(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-manual-sales-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">My Sales</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Sales you recorded yourself. Only you and an administrator can see these.
                    </p>
                </div>

                <button onClick={openNew} className="h-9 px-4 rounded bg-foreground text-background text-sm" data-testid="sale-add-button">
                    Record Sale
                </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <SummaryCard label="Combined Total"
                             value={money(totals.combined_amount)}
                             hint="What you recorded, plus the sales file" />
                <SummaryCard label="Recorded by You"
                             value={money(totals.confirmed_amount)}
                             hint="Draft and cancelled excluded" />
                <SummaryCard label={`From the Sales File${totals.file_year ? ` (${totals.file_year})` : ""}`}
                             value={money(totals.file_amount)}
                             hint={totals.file_rows ? "Imported from the sales workbook" : "No file sales recorded for you"} />
                <SummaryCard label="Confirmed Sales" value={(totals.confirmed_count ?? 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
                <SummaryCard label="Records Shown" value={total.toLocaleString("en-PH", { timeZone: "Asia/Manila" })} hint={hasFilters ? "filtered" : ""} />
            </div>

            <p className="text-xs text-muted-foreground -mt-1">
                The sales-file figure is your whole {totals.file_year || "year"} as credited by the
                imported sales workbook. It is not affected by the filters below, which apply only
                to the sales you recorded yourself.
            </p>

            <form onSubmit={search} className="sc-card p-3 space-y-3">
                <div className="flex flex-col md:flex-row gap-3">
                    <select className="sc-input md:w-52" value={store} onChange={(e) => setStore(e.target.value)} data-testid="sale-filter-store">
                        <option value="">All stores</option>
                        {(facets.stores.length ? facets.stores : facets.all_stores).map((st) => (
                            <option key={st} value={st}>{st}</option>
                        ))}
                    </select>

                    <select className="sc-input md:w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sale-filter-status">
                        <option value="">All statuses</option>
                        {facets.statuses.map((st) => (
                            <option key={st} value={st}>{st}</option>
                        ))}
                    </select>

                    {isAdmin && (
                        <select className="sc-input md:w-52" value={agent} onChange={(e) => setAgent(e.target.value)} data-testid="sale-filter-agent">
                            <option value="">All agents</option>
                            {facets.agents.map((a) => (
                                <option key={a} value={a}>{a}</option>
                            ))}
                        </select>
                    )}

                    <select className="sc-input md:w-40" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sale-sort">
                        <option value="newest">Newest first</option>
                        <option value="oldest">Oldest first</option>
                    </select>
                </div>

                <div className="flex flex-col md:flex-row gap-3">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        From
                        <input type="date" className="sc-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sale-filter-from" />
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        To
                        <input type="date" className="sc-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sale-filter-to" />
                    </label>

                    <div className="relative flex-1">
                        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            className="sc-input pl-9"
                            placeholder="Search customer, contact, quotation no…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="h-9 px-4 rounded border border-border text-sm hover:bg-secondary">Search</button>
                    {hasFilters && (
                        <button type="button" onClick={clearFilters} className="h-9 px-4 rounded border border-border text-sm text-muted-foreground hover:bg-secondary">
                            Clear
                        </button>
                    )}
                </div>
            </form>

            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <div>
                    {loading ? "Loading…" : total === 0 ? "No sales recorded" :
                        `Showing ${offset + 1}–${offset + rows.length} of ${total.toLocaleString("en-PH", { timeZone: "Asia/Manila" })}`}
                    {hasFilters && total > 0 ? " (filtered)" : ""}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => goPage(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0 || loading}
                        className="h-8 px-3 rounded border border-border disabled:opacity-40 hover:bg-secondary">Prev</button>
                    <button onClick={() => goPage(offset + PAGE_SIZE)} disabled={offset + rows.length >= total || loading}
                        className="h-8 px-3 rounded border border-border disabled:opacity-40 hover:bg-secondary">Next</button>
                </div>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1000px]">
                        <thead>
                            <tr>
                                <th>Date / Ref</th>
                                <th>Customer</th>
                                <th>Store</th>
                                <th>Payment</th>
                                {isAdmin && <th>Agent</th>}
                                <th>Status</th>
                                <th className="text-right">Amount</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
                            )}
                            {!loading && errored && (
                                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">Couldn't load sales.</td></tr>
                            )}
                            {!loading && !errored && rows.length === 0 && (
                                <tr>
                                    <td colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">
                                        No sales recorded yet. Use “Record Sale” to add the first one.
                                    </td>
                                </tr>
                            )}

                            {!loading && !errored && rows.map((r) => (
                                <tr key={r.id} data-testid={`sale-row-${r.id}`}>
                                    <td>
                                        <div className="font-medium">{s(r.sale_date)}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{s(r.record_no, "")}</div>
                                    </td>
                                    <td>
                                        <div className="font-medium">{s(r.customer_name)}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {s(r.contact_person, "")}
                                            {r.quotation_no ? ` · Quote ${s(r.quotation_no)}` : ""}
                                            {r.po_number ? ` · PO ${s(r.po_number)}` : ""}
                                            {r.lead_record_no ? (
                                                <span className="inline-flex items-center gap-1 ml-1">
                                                    <Link2 className="w-3 h-3" />{s(r.lead_record_no)}
                                                </span>
                                            ) : null}
                                        </div>
                                    </td>
                                    <td className="text-xs">{s(r.store)}</td>
                                    <td className="text-xs">{s(r.mode_of_payment)}</td>
                                    {isAdmin && <td className="text-xs">{s(r.owner_name)}</td>}
                                    <td>
                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusCls(r.status)}`}>
                                            {s(r.status)}
                                        </span>
                                    </td>
                                    <td className="sc-num">{money(r.amount)}</td>
                                    <td className="text-right whitespace-nowrap">
                                        <button onClick={() => openEdit(r)}
                                            className="h-8 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                            data-testid={`sale-edit-${r.id}`}>
                                            <Pencil className="w-3.5 h-3.5" /> Edit
                                        </button>
                                        <button onClick={() => setConfirmDelete(r)}
                                            className="h-8 ml-1 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs text-rose-600 hover:bg-secondary"
                                            data-testid={`sale-delete-${r.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <SaleModal
                    isEdit={!!editing.id}
                    form={form}
                    updateForm={updateForm}
                    facets={facets}
                    saving={saving}
                    onSave={save}
                    onClose={closeModal}
                />
            )}

            {confirmDelete && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setConfirmDelete(null)}>
                    <div className="sc-card p-5 w-full max-w-md space-y-4 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Delete Sale</div>
                            <div className="text-lg font-semibold">{s(confirmDelete.customer_name)}</div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {money(confirmDelete.amount)} on {s(confirmDelete.sale_date)}. This removes it from your
                            list and from every total. The record is kept for audit.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setConfirmDelete(null)} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={doDelete} className="h-9 px-4 rounded bg-red-600 text-white text-sm" data-testid="sale-delete-confirm">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SaleModal({ isEdit, form, updateForm, facets, saving, onSave, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
            <div className="sc-card p-5 w-full max-w-2xl space-y-4 bg-background max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="sale-modal">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{isEdit ? "Edit Sale" : "Record Sale"}</div>
                    <div className="text-lg font-semibold">{isEdit ? "Update this sale" : "New Sale"}</div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Customer" required full>
                        <input className="sc-input" value={form.customer_name}
                            onChange={(e) => updateForm("customer_name", e.target.value)} data-testid="sale-customer-input" />
                    </Field>

                    <Field label="Contact Person">
                        <input className="sc-input" value={form.contact_person} onChange={(e) => updateForm("contact_person", e.target.value)} />
                    </Field>
                    <Field label="Contact No.">
                        <input className="sc-input" value={form.contact_no} onChange={(e) => updateForm("contact_no", e.target.value)} />
                    </Field>
                    <Field label="Email">
                        <input type="email" className="sc-input" value={form.email} onChange={(e) => updateForm("email", e.target.value)} />
                    </Field>

                    <Field label="Amount" required>
                        <input type="number" step="0.01" min="0" className="sc-input" value={form.amount}
                            onChange={(e) => updateForm("amount", e.target.value)} data-testid="sale-amount-input" />
                    </Field>
                    <Field label="Sale Date" required>
                        <input type="date" className="sc-input" value={form.sale_date}
                            onChange={(e) => updateForm("sale_date", e.target.value)} data-testid="sale-date-input" />
                    </Field>

                    <Field label="Store" required>
                        <select className="sc-input" value={form.store} onChange={(e) => updateForm("store", e.target.value)} data-testid="sale-store-input">
                            <option value="">Select store…</option>
                            {(facets.all_stores || []).map((st) => (
                                <option key={st} value={st}>{st}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Mode of Payment">
                        <select className="sc-input" value={form.mode_of_payment} onChange={(e) => updateForm("mode_of_payment", e.target.value)}>
                            <option value="">Select…</option>
                            {(facets.payment_modes || []).map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Quotation No.">
                        <input className="sc-input" placeholder="Your own reference" value={form.quotation_no}
                            onChange={(e) => updateForm("quotation_no", e.target.value)} />
                    </Field>

                    <Field label="PO No.">
                        <input className="sc-input" placeholder="Customer PO number" value={form.po_number}
                            onChange={(e) => updateForm("po_number", e.target.value)} data-testid="sale-po-input" />
                    </Field>

                    <Field label="Status">
                        <select className="sc-input" value={form.status} onChange={(e) => updateForm("status", e.target.value)} data-testid="sale-status-input">
                            {(facets.statuses || ["Draft", "Confirmed", "Cancelled"]).map((st) => (
                                <option key={st} value={st}>{st}</option>
                            ))}
                        </select>
                    </Field>

                    <div className="md:col-span-2">
                        <OpportunityPicker
                            dealId={form.deal_id}
                            dealLabel={form.deal_record_no}
                            onPick={(deal) => {
                                if (!deal) {
                                    updateForm("deal_id", "");
                                    updateForm("deal_record_no", "");
                                    return;
                                }
                                updateForm("deal_id", deal.id);
                                updateForm("deal_record_no", deal.record_no || "");
                                // Pull the details from the Won opportunity into the sale.
                                updateForm("customer_name", deal.account_name || deal.deal_name || "");
                                if (deal.contact_name) updateForm("contact_person", deal.contact_name);
                                if (deal.amount != null) updateForm("amount", deal.amount);
                                if (deal.quotation_no) updateForm("quotation_no", deal.quotation_no);
                            }}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <LeadPicker
                            leadId={form.lead_id}
                            leadLabel={form.lead_record_no}
                            onPick={(lead) => {
                                updateForm("lead_id", lead ? lead.id : "");
                                updateForm("lead_record_no", lead ? (lead.record_no || "") : "");
                                if (lead && !form.customer_name) updateForm("customer_name", lead.company_name || "");
                            }}
                        />
                    </div>

                    <Field label="Notes" full>
                        <textarea className="sc-input h-auto py-2" rows={2} value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} />
                    </Field>
                </div>

                <p className="text-xs text-muted-foreground">
                    Only <span className="font-medium text-foreground">Confirmed</span> sales count toward your totals
                    and the comparison against SAP.
                </p>

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                    <button onClick={onSave} disabled={saving}
                        className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="sale-save">
                        {saving ? "Saving…" : isEdit ? "Save Changes" : "Record Sale"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Attach a sale to one of the caller's OWN leads. The leads endpoint is already
// scope-enforced server-side, so this can never surface another agent's leads.
function LeadPicker({ leadId, leadLabel, onPick }) {
    const [term, setTerm] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!term.trim()) { setResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/crm/leads", { params: { q: term, limit: 8, include_converted: 1 } });
                setResults(Array.isArray(data?.items) ? data.items : []);
                setOpen(true);
            } catch { setResults([]); }
        }, 300);
        return () => clearTimeout(t);
    }, [term]);

    if (leadId) {
        return (
            <div className="space-y-1">
                <div className="text-xs font-medium">Linked Lead</div>
                <div className="flex items-center justify-between gap-2 rounded border border-border px-3 h-9">
                    <span className="text-xs font-mono">{leadLabel || leadId}</span>
                    <button type="button" onClick={() => onPick(null)} className="text-muted-foreground hover:text-foreground" title="Unlink">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-1 relative">
            <div className="text-xs font-medium">Link a Lead <span className="text-muted-foreground font-normal">(optional)</span></div>
            <input
                className="sc-input"
                placeholder="Search your leads by name, company or email…"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onFocus={() => results.length && setOpen(true)}
                data-testid="sale-lead-picker"
            />
            {open && results.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 sc-card bg-background border border-border max-h-52 overflow-y-auto divide-y divide-border">
                    {results.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => { onPick(l); setOpen(false); setTerm(""); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                        >
                            <div className="font-medium">
                                {s(l.company_name, "") || `${s(l.first_name, "")} ${s(l.last_name, "")}`.trim() || "(unnamed lead)"}
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">{s(l.record_no, "")}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Source a sale from one of the caller's OWN Won opportunities. The deals endpoint is
// scope-enforced server-side, so this only ever surfaces the caller's own won deals.
function OpportunityPicker({ dealId, dealLabel, onPick }) {
    const [term, setTerm] = useState("");
    const [deals, setDeals] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/crm/deals", { params: { stage: "Closed Won" } });
                setDeals(Array.isArray(data) ? data : []);
            } catch { setDeals([]); }
        })();
    }, []);

    const results = (term.trim()
        ? deals.filter((d) => {
            const t = term.toLowerCase();
            return [d.account_name, d.deal_name, d.contact_name, d.quotation_no]
                .some((x) => String(x || "").toLowerCase().includes(t));
        })
        : deals).slice(0, 8);

    if (dealId) {
        return (
            <div className="space-y-1">
                <div className="text-xs font-medium">Sourced from Won Opportunity</div>
                <div className="flex items-center justify-between gap-2 rounded border border-border px-3 h-9">
                    <span className="text-xs font-mono">{dealLabel || dealId}</span>
                    <button type="button" onClick={() => onPick(null)} className="text-muted-foreground hover:text-foreground" title="Unlink">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-1 relative">
            <div className="text-xs font-medium">Get details from a Won Opportunity <span className="text-muted-foreground font-normal">(optional)</span></div>
            <input
                className="sc-input"
                placeholder={deals.length ? "Search your won opportunities by customer, deal or quotation…" : "No won opportunities yet"}
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onFocus={() => setOpen(true)}
                data-testid="sale-opportunity-picker"
            />
            {open && results.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 sc-card bg-background border border-border max-h-52 overflow-y-auto divide-y divide-border">
                    {results.map((d) => (
                        <button
                            key={d.id}
                            type="button"
                            onClick={() => { onPick(d); setOpen(false); setTerm(""); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                        >
                            <div className="font-medium">{s(d.account_name, "") || s(d.deal_name, "(deal)")}</div>
                            <div className="text-xs text-muted-foreground">
                                {money(d.amount)}
                                {d.quotation_no ? ` · Quote ${s(d.quotation_no)}` : ""}
                                {d.record_no ? ` · ${s(d.record_no)}` : ""}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function SummaryCard({ label, value, hint }) {
    return (
        <div className="sc-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold mt-1">{value}</div>
            {hint ? <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div> : null}
        </div>
    );
}

function Field({ label, required = false, full = false, children }) {
    return (
        <label className={`space-y-1 block ${full ? "md:col-span-2" : ""}`}>
            <div className="text-xs font-medium">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </div>
            {children}
        </label>
    );
}
